import React, { useCallback, useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";

import {
  Box,
  Button,
  Flex,
  Grid,
  Label,
  FileUpload,
  FormHeading,
  ErrorMsg,
  Toastify,
} from "atoms";
import { FormCheckbox } from "molecules/FormInput";
import { useParams } from "react-router-dom";
import { usePropsoch } from "contexts/Propsoch";

const CHECKS = [
  {
    title: "Regulatory Compliances",
    checks: [
      {
        label: "BBMP E Katha registration for land",
        value: "bbmp",
      },
      { label: "Change of Land Use", value: "changeOfLandUse" },
      { label: "Development Plan", value: "developmentPlan" },
      { label: "Land Conversion", value: "landConversion" },
      { label: "Building Plan", value: "buildingPlan" },
      { label: "Water Noc", value: "waterNoc" },
      { label: "Panchayat Noc (only for plots)", value: "panchayatNoc" },
      {
        label: "Horticulture Noc ( Only for plots )",
        value: "horticultureNoc",
      },
      { label: "KSPCB", value: "kspcb" },
      { label: "MOEF", value: "moef" },
      { label: "Fire Noc", value: "fireNoc" },
      { label: "Consent for Establishment", value: "consentForEstablishment" },
      { label: "BESCOM Noc", value: "bescomNoc" },
      { label: "BSNL Noc", value: "bsnlNoc" },
      { label: "Height Noc - AAI", value: "heightNocAAI" },
      { label: "Height Noc- IAF", value: "heightNocIAF" },
      { label: "Height Noc- HAL", value: "heightNocHal" },
      { label: "BMRCL", value: "bmrcl" },
      { label: "Commencement Certificate", value: "commercialCertificate" },
    ],
  },
  {
    title: "Completion Approvals",
    checks: [
      { label: "Fire Final NOC", value: "fireFinalNoc" },
      { label: "Consent for Operation", value: "consentForOperation" },
      { label: "Power Connection", value: "powerConnection" },
      { label: "CEIG approval- Lifts", value: "ceigApprovalLifts" },
      {
        label: "BBMP Occupancy Certificate",
        value: "bbmpOccupencyCertificate",
      },
      {
        label: "Site Release (Applicable only for Plotted)",
        value: "siteRelease",
      },
    ],
  },
];

const validationSchema = Yup.object().shape({
  compliances: Yup.array()
    .min(1, "Choose a compliance")
    .typeError("Compliance is required")
    .required("Compliance is required"),
});

export const CompliancesAndApprovalsForm = ({
  onNext,
  onPrevious,
  bindHitOnSave,
  fetchProperty,
  rootProperty,
}) => {
  const {
    actions: {
      getPropertyListingCompliances,
      createPropertyListingCompliances,
      updatePropertyListingCompliances,
    },
  } = usePropsoch();

  const { id } = useParams();

  // fetch property
  const [property, setProperty] = useState(null);

  useEffect(() => {
    id &&
      getPropertyListingCompliances(id)?.then((data) => {
        setProperty(data);

        const compliances = [];
        const compliancesData = {};

        data?.compliances?.forEach((item) => {
          if (item?.value) {
            compliances.push(item?.type);
          }
          compliancesData[item?.type] = {};
          compliancesData[item?.type].images = item?.images;
        });

        // reformat type
        data &&
          setProperty((prev) => ({
            ...prev,
            data: data,
            compliances: compliances,
            compliancesData: compliancesData,
          }));
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // submit
  const [submitLoading, setSubmitLoading] = useState(false);
  const onSubmit = useCallback(
    ({ compliances, compliancesData, dirty }) => {
      setSubmitLoading(true);
      if (dirty) {
        const compliancesArr = compliances?.map((compliance) => ({
          type: compliance,
          value: true,
          images: compliancesData?.[compliance]?.images,
        }));
        property &&
          property?.data?.compliances
            ?.filter((i) => i?.value)
            ?.filter((i) => !!!compliancesArr?.find((j) => j?.type === i?.type))
            ?.forEach((i) => {
              compliancesArr?.push({ ...i, value: false });
            });

        const formattedValues = {
          propertyId: id,
          compliances: compliancesArr,
        };

        if (property) {
          updatePropertyListingCompliances(
            { ...formattedValues },
            id,
            rootProperty?.name
          )
            .then(() => {
              Toastify("success", "Compliances & approvals updated");
              fetchProperty()?.then(() => {
                onNext();
              });
            })
            .catch((e) => {
              Toastify("error", "Compliances & approvals not updated");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        } else {
          createPropertyListingCompliances(formattedValues, rootProperty?.name)
            .then(() => {
              Toastify("success", "Compliances & approvals created");
              fetchProperty()?.then(() => {
                onNext();
              });
            })
            .catch((e) => {
              Toastify("error", "Compliances & approvals not created");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        }
      } else {
        if (id) {
          onNext();
        }
      }
    },
    [
      createPropertyListingCompliances,
      id,
      updatePropertyListingCompliances,
      onNext,
      property,
      rootProperty,
      fetchProperty,
    ]
  );

  return (
    <Formik
      initialValues={{ compliances: [], compliancesData: {}, ...property }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ values, setFieldValue, submitForm, dirty, touched, errors }) => {
        bindHitOnSave(() => {
          setFieldValue("dirty", dirty);
          submitForm();
        });
        return (
          <Form>
            <FormHeading text="Compliances & Approvals" />
            <Grid gridGap="3.2rem" mt="2.4rem">
              {CHECKS?.map((check, index) => (
                <Grid key={index} gridGap="2.4rem">
                  <Label text={check?.title} req mb="-0.4rem" />
                  {check?.checks?.map((checklist, checklistIndex) => (
                    <Box key={checklistIndex}>
                      <FormCheckbox
                        name="compliances"
                        height="1.8rem"
                        label={checklist?.label}
                        value={checklist?.value}
                        mb="1.6rem"
                      />
                      {[...values?.compliances]?.find(
                        (i) => i === checklist?.value
                      ) && (
                        <FileUpload
                          files={
                            values?.compliancesData?.[checklist?.value]?.images
                          }
                          onUpload={(locations) => {
                            const newProjectImages = [
                              ...(values?.compliancesData?.[checklist?.value]
                                ?.images
                                ? JSON.parse(
                                    JSON.stringify(
                                      values?.compliancesData?.[
                                        checklist?.value
                                      ]?.images
                                    )
                                  ) || []
                                : []),
                              ...locations?.map((i) => ({ image: i, alt: "" })),
                            ];

                            setFieldValue(
                              `compliancesData.${checklist?.value}.images`,
                              newProjectImages
                            );
                          }}
                          onSubmitDescription={({ alt, file }) => {
                            setFieldValue(
                              `compliancesData.${checklist?.value}.images.[${file?.index}].alt`,
                              alt
                            );
                          }}
                          onCross={(index) => {
                            const currProjectImages = [
                              ...(values?.compliancesData?.[checklist?.value]
                                ?.images
                                ? JSON.parse(
                                    JSON.stringify(
                                      values?.compliancesData?.[
                                        checklist?.value
                                      ]?.images
                                    )
                                  ) || []
                                : []),
                            ];
                            const total = currProjectImages?.length;

                            const newProjectImages = [
                              ...currProjectImages?.slice(0, index),
                              ...currProjectImages?.slice(index + 1, total),
                            ];

                            setFieldValue(
                              `compliancesData.${checklist?.value}.images`,
                              newProjectImages
                            );
                          }}
                          inputFormat="application/pdf"
                          ext={["pdf"]}
                        />
                      )}
                    </Box>
                  ))}
                  {!index && touched?.compliances && errors?.compliances && (
                    <ErrorMsg text={errors?.compliances} />
                  )}
                </Grid>
              ))}

              <Flex my="2rem" gridGap="1.6rem">
                <Button
                  px="3rem"
                  width="fit-content"
                  variant="primaryOutline"
                  height="4.8rem"
                  onClick={onPrevious}
                >
                  Previous
                </Button>
                <Button
                  px="3rem"
                  width="fit-content"
                  variant="primary"
                  height="4.8rem"
                  type="submit"
                  loading={submitLoading}
                  onClick={() => {
                    setFieldValue("dirty", dirty);
                    submitForm();
                  }}
                >
                  Next
                </Button>
              </Flex>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
